<div class="trip-scheduling">
  <div class="page-header">
    <div>
      <h1>Trip Scheduling</h1>
      <p class="text-muted">Schedule active routes with a vehicle and responsible driver from the same organisation.</p>
    </div>
    <div class="header-actions">
      <button class="btn btn-primary" type="button" (click)="startCreateTrip()">
        <i class="material-icons">add</i>
        Schedule Trip
      </button>
      <button class="btn btn-secondary" type="button" (click)="startCreateRecurringSchedule()">
        <i class="material-icons">event_repeat</i>
        Recurring Schedule
      </button>
    </div>
  </div>

  <div class="save-banner" *ngIf="successMessage">
    <i class="material-icons">check_circle</i>
    {{ successMessage }}
  </div>
  <div class="save-banner error" *ngIf="errorMessage">
    <i class="material-icons">error</i>
    {{ errorMessage }}
  </div>

  <div class="calendar-section">
    <div class="calendar-header">
      <h3>Trip Calendar</h3>
      <div class="calendar-nav">
        <button class="btn-icon" type="button" (click)="previousCalendarMonth()" title="Previous month"><i class="material-icons">chevron_left</i></button>
        <span class="current-month">{{ getCalendarTitle() }}</span>
        <button class="btn-icon" type="button" (click)="nextCalendarMonth()" title="Next month"><i class="material-icons">chevron_right</i></button>
      </div>
    </div>
    
    <div class="calendar-weekdays">
      <span *ngFor="let weekday of getCalendarWeekdays()">{{ weekday }}</span>
    </div>
    <div class="calendar-grid">
      <div class="calendar-day" *ngFor="let day of getCalendarDays()" [class.muted-day]="!day.isCurrentMonth" [class.today]="day.isToday">
        <div class="day-header">
          <span>{{ day.dayNumber }}</span>
          <small *ngIf="day.trips.length">{{ day.trips.length }} trip{{ day.trips.length === 1 ? '' : 's' }}</small>
        </div>
        <div class="day-trips" *ngIf="day.trips.length; else noDayTrips">
          <button class="trip-event" type="button" *ngFor="let trip of day.trips" (click)="editTrip(trip)">
            <span class="trip-time">{{ getTripCalendarTime(trip) }}</span>
            <span class="trip-route">{{ getTripRouteName(trip) }}</span>
            <small>{{ getTripVehicleLabel(trip) }} / {{ getTripDriverName(trip) }}</small>
          </button>
        </div>
        <ng-template #noDayTrips>
          <span class="no-trips">No trips</span>
        </ng-template>
      </div>
    </div>
  </div>

  <div class="trip-form-container" *ngIf="showForm">
    <div class="form-header">
      <h2>{{ duplicateSource ? 'Duplicate Trip' : isEditing ? 'Edit Trip' : 'Schedule Trip' }}</h2>
      <button class="btn-close" type="button" (click)="resetForm()">×</button>
    </div>
    
    <form [formGroup]="tripForm" (ngSubmit)="onSubmit()" class="trip-form">
      <div class="save-banner error" *ngIf="hasTripFormErrors">
        <i class="material-icons">error</i>
        Complete the highlighted fields before saving this trip.
      </div>
      <p class="text-muted" *ngIf="duplicateSource">Bookings and live trip activity will not be copied. Choose the new trip times below.</p>
      <div class="form-row">
        <div class="form-group">
          <label for="tripName">Trip name *</label>
          <input id="tripName" type="text" formControlName="tripName" class="form-control" placeholder="Morning CBD Express">
          <span class="error" *ngIf="isInvalid('tripName')">Trip name is required.</span>
        </div>
        <div class="form-group">
          <label for="routeID">Active route *</label>
          <select id="routeID" formControlName="routeID" class="form-control" [attr.disabled]="duplicateSource ? true : null">
            <option [ngValue]="null">Select a route</option>
            <option *ngFor="let route of getFilteredRoutes()" [ngValue]="route.routeID">
              {{ route.routeName }}
            </option>
          </select>
          <span class="error" *ngIf="isInvalid('routeID')">Active route is required.</span>
        </div>
      </div>

      <div class="form-row">
        <div class="form-group">
          <label for="vehicleID">Vehicle *</label>
          <select id="vehicleID" formControlName="vehicleID" class="form-control">
            <option [ngValue]="null">Select a vehicle</option>
            <option *ngFor="let vehicle of getFilteredVehicles()" [ngValue]="vehicle.vehicleID">
              {{ vehicle.vehicleRegistration }} ({{ vehicle.vehicleManufacturerMake }} {{ vehicle.vehicleManufacturerModel }})
            </option>
          </select>
          <span class="error" *ngIf="isInvalid('vehicleID')">Vehicle is required.</span>
        </div>
        
        <div class="form-group">
          <label for="driverID">Active driver *</label>
          <select id="driverID" formControlName="driverID" class="form-control">
            <option [ngValue]="null">Select a driver</option>
            <option *ngFor="let driver of drivers" [ngValue]="driver.userID">
              {{ getDriverName(driver) }}
            </option>
          </select>
          <span class="error" *ngIf="isInvalid('driverID')">Active driver is required.</span>
          <span class="error" *ngIf="driverValidationMessage">{{ driverValidationMessage }}</span>
        </div>
      </div>

      <div class="save-banner error" *ngIf="organisationValidationMessage">
        <i class="material-icons">error</i>
        {{ organisationValidationMessage }}
      </div>

      <div class="form-row">
        <div class="form-group">
          <label for="tripCapacity">Capacity *</label>
          <input id="tripCapacity" type="number" formControlName="tripCapacity" class="form-control" min="1" [attr.disabled]="duplicateSource ? true : null">
          <span class="error" *ngIf="isInvalid('tripCapacity')">Capacity is required.</span>
        </div>
        <div class="form-group">
          <label for="tripSeatsAvailable">Seats available *</label>
          <input id="tripSeatsAvailable" type="number" formControlName="tripSeatsAvailable" class="form-control" min="0" [attr.disabled]="duplicateSource ? true : null">
          <span class="error" *ngIf="isInvalid('tripSeatsAvailable')">Seats available is required.</span>
        </div>
      </div>

      <div class="form-row">
        <div class="form-group">
          <label for="tripStartTime">Start time *</label>
          <input id="tripStartTime" type="datetime-local" formControlName="tripStartTime" class="form-control">
          <span class="error" *ngIf="isInvalid('tripStartTime')">Start time is required.</span>
        </div>
        
        <div class="form-group">
          <label for="tripEndTime">End time *</label>
          <input id="tripEndTime" type="datetime-local" formControlName="tripEndTime" class="form-control">
          <span class="error" *ngIf="isInvalid('tripEndTime')">End time is required.</span>
        </div>
      </div>

      <div class="form-actions">
        <button type="button" class="btn btn-secondary" (click)="resetForm()">Cancel</button>
        <button type="submit" class="btn btn-primary">
          {{ duplicateSource ? 'Duplicate Trip' : isEditing ? 'Update Trip' : 'Schedule Trip' }}
        </button>
      </div>
    </form>
  </div>

  <div class="trip-form-container" *ngIf="showRecurringForm">
    <div class="form-header">
      <div>
        <h2>{{ selectedRecurringSchedule ? 'Edit Recurring Schedule' : 'Create Recurring Schedule' }}</h2>
        <p class="text-muted">Generate individual trips automatically from a reusable schedule.</p>
      </div>
      <button class="btn-close" type="button" (click)="showRecurringForm = false">×</button>
    </div>

    <form [formGroup]="recurringForm" (ngSubmit)="saveRecurringSchedule()" class="trip-form">
      <div class="form-row">
        <div class="form-group">
          <label for="tripNameTemplate">Trip name *</label>
          <input id="tripNameTemplate" class="form-control" formControlName="tripNameTemplate" [placeholder]="'Morning Express {date}'">
          <small class="text-muted">Use <code>{{ '{date}' }}</code> to include the occurrence date.</small>
          <span class="error" *ngIf="isRecurringInvalid('tripNameTemplate')">Trip name is required.</span>
        </div>
        <div class="form-group">
          <label for="recurringCapacity">Capacity *</label>
          <input id="recurringCapacity" class="form-control" type="number" min="1" formControlName="tripCapacity">
          <span class="error" *ngIf="isRecurringInvalid('tripCapacity')">Capacity must be greater than zero.</span>
        </div>
      </div>

      <div class="form-row">
        <div class="form-group">
          <label for="recurringRoute">Active route *</label>
          <select id="recurringRoute" class="form-control" formControlName="routeID">
            <option [ngValue]="null">Select a route</option>
            <option *ngFor="let route of getFilteredRecurringRoutes()" [ngValue]="route.routeID">{{ route.routeName }}</option>
          </select>
          <span class="error" *ngIf="isRecurringInvalid('routeID')">Route is required.</span>
        </div>
        <div class="form-group">
          <label for="recurringVehicle">Vehicle *</label>
          <select id="recurringVehicle" class="form-control" formControlName="vehicleID">
            <option [ngValue]="null">Select a vehicle</option>
            <option *ngFor="let vehicle of getFilteredRecurringVehicles()" [ngValue]="vehicle.vehicleID">{{ vehicle.vehicleRegistration }} ({{ vehicle.vehicleManufacturerMake }} {{ vehicle.vehicleManufacturerModel }})</option>
          </select>
          <span class="error" *ngIf="isRecurringInvalid('vehicleID')">Vehicle is required.</span>
        </div>
      </div>

      <div class="form-row">
        <div class="form-group">
          <label for="recurringDriver">Active driver *</label>
          <select id="recurringDriver" class="form-control" formControlName="driverID">
            <option [ngValue]="null">Select a driver</option>
            <option *ngFor="let driver of recurringDrivers" [ngValue]="driver.userID">{{ getDriverName(driver) }}</option>
          </select>
          <span class="error" *ngIf="isRecurringInvalid('driverID')">Driver is required.</span>
        </div>
        <div class="form-group">
          <label for="frequency">Frequency *</label>
          <select id="frequency" class="form-control" formControlName="frequency">
            <option value="Daily">Daily</option>
            <option value="EveryNDays">Every number of days</option>
            <option value="Weekly">Weekly</option>
          </select>
        </div>
      </div>

      <div class="form-row">
        <div class="form-group">
          <label for="interval">Repeat every *</label>
          <div class="input-suffix">
            <input id="interval" class="form-control" type="number" min="1" formControlName="interval">
            <span>{{ recurringForm.value.frequency === 'Weekly' ? 'week(s)' : 'day(s)' }}</span>
          </div>
          <span class="error" *ngIf="isRecurringInvalid('interval')">Interval must be at least one.</span>
        </div>
        <div class="form-group" *ngIf="recurringForm.value.frequency === 'Weekly'">
          <label>Days of the week *</label>
          <div class="weekday-picker">
            <label *ngFor="let day of weekdays">
              <input type="checkbox" [checked]="isWeekdaySelected(day.value)" (change)="toggleWeekday(day.value, $any($event.target).checked)">
              <span>{{ day.label }}</span>
            </label>
          </div>
          <span class="error" *ngIf="recurringSubmissionAttempted && !selectedWeekdays.size">Select at least one weekday.</span>
        </div>
      </div>

      <div class="form-row">
        <div class="form-group">
          <label for="recurringStartDate">Start date *</label>
          <input id="recurringStartDate" class="form-control" type="date" formControlName="startDate">
          <span class="error" *ngIf="isRecurringInvalid('startDate')">Start date is required.</span>
        </div>
        <div class="form-group">
          <label for="recurringEndDate">End date</label>
          <input id="recurringEndDate" class="form-control" type="date" formControlName="endDate">
        </div>
      </div>

      <div class="form-row">
        <div class="form-group">
          <label for="recurringStartTime">Departure time *</label>
          <input id="recurringStartTime" class="form-control" type="time" formControlName="startTime">
          <span class="error" *ngIf="isRecurringInvalid('startTime')">Departure time is required.</span>
        </div>
        <div class="form-group">
          <label for="durationMinutes">Trip duration in minutes *</label>
          <input id="durationMinutes" class="form-control" type="number" min="1" max="1439" formControlName="durationMinutes">
          <span class="error" *ngIf="isRecurringInvalid('durationMinutes')">Enter a duration between 1 and 1,439 minutes.</span>
        </div>
      </div>

      <div class="form-row">
        <div class="form-group">
          <label for="occurrenceCount">Stop after number of trips</label>
          <input id="occurrenceCount" class="form-control" type="number" min="1" formControlName="occurrenceCount" placeholder="No limit">
        </div>
        <div class="form-group">
          <label for="generateDaysAhead">Generate days ahead *</label>
          <input id="generateDaysAhead" class="form-control" type="number" min="1" max="365" formControlName="generateDaysAhead">
        </div>
      </div>

      <div class="form-actions">
        <button type="button" class="btn btn-secondary" (click)="showRecurringForm = false">Cancel</button>
        <button type="submit" class="btn btn-primary" [disabled]="isSavingRecurring">{{ isSavingRecurring ? 'Saving...' : 'Save Recurring Schedule' }}</button>
      </div>
    </form>
  </div>

  <div class="trips-list recurring-list" *ngIf="recurringSchedules.length">
    <div class="list-header">
      <div><h3>Recurring Schedules ({{ recurringSchedules.length }})</h3><p class="text-muted">Schedules automatically create normal operational trips.</p></div>
    </div>
    <div class="trips-table">
      <table>
        <thead><tr><th>Schedule</th><th>Route</th><th>Driver</th><th>Pattern</th><th>Starts</th><th>Status</th><th>Actions</th></tr></thead>
        <tbody>
          <tr *ngFor="let schedule of recurringSchedules" [class.inactive-row]="isRecurringInactive(schedule)">
            <td><strong>{{ schedule.tripNameTemplate }}</strong><small class="inactive-note">{{ schedule.startTime }}</small></td>
            <td>{{ schedule.routeName || ('Route ' + schedule.routeID) }}</td>
            <td>{{ schedule.driverName || ('Driver ' + schedule.driverID) }}</td>
            <td>{{ getRecurrenceLabel(schedule) }}</td>
            <td>{{ schedule.startDate | date:'mediumDate' }}</td>
            <td><span class="status-badge">{{ schedule.statusName || (isRecurringInactive(schedule) ? 'Inactive' : 'Active') }}</span></td>
            <td class="actions-cell">
              <button class="btn-icon" type="button" title="Generate upcoming trips" (click)="generateRecurringTrips(schedule)" [disabled]="isRecurringInactive(schedule)"><i class="material-icons">refresh</i></button>
              <button class="btn-icon" type="button" title="Edit schedule" (click)="editRecurringSchedule(schedule)"><i class="material-icons">edit</i></button>
              <button class="btn-icon btn-danger" type="button" title="Deactivate schedule" (click)="deactivateRecurringSchedule(schedule)" [disabled]="isRecurringInactive(schedule)"><i class="material-icons">archive</i></button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <div class="trips-list">
    <div class="list-header">
      <h3>Upcoming Trips ({{ upcomingTrips.length }})</h3>
    </div>

    <div class="trips-table" *ngIf="upcomingTrips.length">
      <table>
        <thead>
          <tr>
            <th>Trip</th>
            <th>Route</th>
            <th>Vehicle</th>
            <th>Driver</th>
            <th>Start</th>
            <th>Seats</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let trip of upcomingTrips" [class.inactive-row]="!getTripDriverId(trip)">
            <td>
              <strong>{{ trip.tripName }}</strong>
              <p class="text-muted">ID {{ trip.tripID }}</p>
            </td>
            <td>{{ getTripRouteName(trip) }}</td>
            <td>{{ getTripVehicleLabel(trip) }}</td>
            <td>
              <span [class.error]="!getTripDriverId(trip)">{{ getTripDriverName(trip) }}</span>
              <small class="inactive-note" *ngIf="!getTripDriverId(trip)">Driver required before editing save</small>
            </td>
            <td>{{ getTripStart(trip) | date:'short' }}</td>
            <td>{{ trip.tripSeatsAvailable ?? trip.availableSeats }}/{{ trip.tripCapacity ?? trip.availableSeats }}</td>
            <td>
              <span class="status-badge">{{ getTripStatusLabel(trip) }}</span>
            </td>
            <td class="actions-cell">
              <button class="btn-icon" type="button" (click)="editTrip(trip)" title="Edit trip">
                <i class="material-icons">edit</i>
              </button>
              <button class="btn-icon" type="button" (click)="startDuplicateTrip(trip)" title="Duplicate trip">
                <i class="material-icons">content_copy</i>
              </button>
              <button class="btn-icon btn-danger" type="button" (click)="deleteTrip(trip)" title="Delete trip">
                <i class="material-icons">delete</i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="empty-state" *ngIf="!upcomingTrips.length">
      <i class="material-icons">event_busy</i>
      <h3>No upcoming trips</h3>
      <p>Schedule a future trip with an active route, vehicle, and driver.</p>
      <button class="btn btn-primary" type="button" (click)="startCreateTrip()">Schedule Trip</button>
    </div>
  </div>
</div>
